import { useEffect, useMemo } from 'react'
import { usedRange, useStore } from '../core/store'
import { rangeToA1 } from '../core/a1'
import {
  MARGIN_MM,
  PAPER_LABELS,
  estimatePages,
  pageMetrics,
  rangeHeightPx,
  rangeWidthPx,
} from '../core/pdf'
import type { MarginName, Orientation, PaperName, PdfOptions, Scope } from '../core/pdf'

const PAPERS: PaperName[] = ['A4', 'A3', 'B5', 'Letter', 'Legal']
const MARGIN_LABELS: Record<MarginName, string> = { narrow: '좁게', normal: '보통', wide: '넓게' }

export default function PdfDialog({
  options,
  onChange,
  onClose,
  onPrint,
}: {
  options: PdfOptions
  onChange: (patch: Partial<PdfOptions>) => void
  onClose: () => void
  onPrint: () => void
}) {
  const wb = useStore((s) => s.wb)
  const version = useStore((s) => s.version)
  const selRange = useStore((s) => s.selRange)
  const sheet = wb.sheets.find((s) => s.id === wb.activeSheetId) ?? wb.sheets[0]
  const sel = selRange()

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const est = useMemo(() => {
    const m = pageMetrics(options)
    if (options.scope === 'workbook') {
      let total = 0
      for (const s of wb.sheets) {
        const u = usedRange(s)
        if (!u) continue
        total += estimatePages(
          options,
          m,
          rangeWidthPx(s, u, options.rowColHeaders),
          rangeHeightPx(s, u, options.rowColHeaders)
        ).total
      }
      return { total: Math.max(1, total), zoom: null as number | null }
    }
    const r = options.scope === 'selection' ? sel : usedRange(sheet)
    if (!r) return { total: 0, zoom: null }
    const e = estimatePages(
      options,
      m,
      rangeWidthPx(sheet, r, options.rowColHeaders),
      rangeHeightPx(sheet, r, options.rowColHeaders)
    )
    return { total: e.total, zoom: e.zoom }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, wb, version, sheet.id, sel.r1, sel.c1, sel.r2, sel.c2])

  const used = usedRange(sheet)
  const scopeLabel: Record<Scope, string> = {
    sheet: `현재 시트 — ${sheet.name} (${used ? rangeToA1(used) : '비어있음'})`,
    selection: `선택 영역 — ${rangeToA1(sel)}`,
    workbook: `전체 워크북 — 시트 ${wb.sheets.length}개`,
  }

  return (
    <div className="modal-backdrop" onMouseDown={onClose}>
      <div className="modal pdf-modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          🖨 PDF 만들기
          <button className="modal-x" onClick={onClose} title="닫기">
            ✕
          </button>
        </div>

        <div className="modal-body">
          <label className="fld">
            <span>인쇄 범위</span>
            <select value={options.scope} onChange={(e) => onChange({ scope: e.target.value as Scope })}>
              {(['sheet', 'selection', 'workbook'] as Scope[]).map((s) => (
                <option key={s} value={s}>
                  {scopeLabel[s]}
                </option>
              ))}
            </select>
          </label>

          <div className="fld-row">
            <label className="fld">
              <span>용지</span>
              <select value={options.paper} onChange={(e) => onChange({ paper: e.target.value as PaperName })}>
                {PAPERS.map((p) => (
                  <option key={p} value={p}>
                    {PAPER_LABELS[p]}
                  </option>
                ))}
              </select>
            </label>
            <label className="fld">
              <span>방향</span>
              <select
                value={options.orientation}
                onChange={(e) => onChange({ orientation: e.target.value as Orientation })}
              >
                <option value="portrait">세로</option>
                <option value="landscape">가로</option>
              </select>
            </label>
            <label className="fld">
              <span>여백</span>
              <select value={options.margin} onChange={(e) => onChange({ margin: e.target.value as MarginName })}>
                {(Object.keys(MARGIN_LABELS) as MarginName[]).map((m) => (
                  <option key={m} value={m}>
                    {MARGIN_LABELS[m]} ({MARGIN_MM[m]}mm)
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="fld-row">
            <label className="fld">
              <span>배율</span>
              <select value={options.fit} onChange={(e) => onChange({ fit: e.target.value as PdfOptions['fit'] })}>
                <option value="width">너비를 한 페이지에 맞춤</option>
                <option value="page">전체를 한 페이지에 맞춤</option>
                <option value="none">직접 지정</option>
              </select>
            </label>
            {options.fit === 'none' && (
              <label className="fld narrow">
                <span>%</span>
                <input
                  type="number"
                  min={10}
                  max={400}
                  step={5}
                  value={options.scale}
                  onChange={(e) => onChange({ scale: Number(e.target.value) || 100 })}
                />
              </label>
            )}
            <label className="fld narrow">
              <span>반복 머리글 행</span>
              <input
                type="number"
                min={0}
                max={5}
                value={options.repeatRows}
                onChange={(e) => onChange({ repeatRows: Math.max(0, Number(e.target.value) || 0) })}
              />
            </label>
          </div>

          <div className="chk-row">
            <label className="chk">
              <input
                type="checkbox"
                checked={options.gridlines}
                onChange={(e) => onChange({ gridlines: e.target.checked })}
              />
              격자선
            </label>
            <label className="chk">
              <input
                type="checkbox"
                checked={options.rowColHeaders}
                onChange={(e) => onChange({ rowColHeaders: e.target.checked })}
              />
              행·열 머리글 (A·B·C / 1·2·3)
            </label>
            <label className="chk">
              <input
                type="checkbox"
                checked={options.showTitle}
                onChange={(e) => onChange({ showTitle: e.target.checked })}
              />
              시트 이름 제목
            </label>
          </div>

          <div className="pdf-est">
            예상 <b>{est.total}</b>페이지
            {est.zoom != null && est.zoom < 1 && <> · 축소 {Math.round(est.zoom * 100)}%</>}
            {est.total === 0 && ' — 인쇄할 내용이 없습니다'}
          </div>

          <p className="pdf-note">
            인쇄 대화상자에서 <b>대상 = "PDF로 저장"</b> 을 고르세요. 페이지 번호·날짜가 필요하면 같은
            대화상자의 <b>머리글 및 바닥글</b> 을 켜면 됩니다. 글자는 이미지가 아니라 텍스트로 들어가므로
            PDF 안에서 검색·복사가 됩니다.
          </p>
        </div>

        <div className="modal-foot">
          <button className="btn" onClick={onClose}>
            취소
          </button>
          <button className="btn primary" disabled={est.total === 0} onClick={onPrint}>
            PDF로 저장
          </button>
        </div>
      </div>
    </div>
  )
}
